/**
 * Subscriptions keeps provider plan health separate from recurring-order sales.
 * A plan source can prove status, starts, endings, and scheduled charges without
 * proving which imported orders belong to those plans. The page only combines
 * those populations after the API explicitly marks their linkage reliable.
 *
 * Card JSX lives in cards/subscriptions.tsx (shared with the Card Library
 * previews); every card site here is wrapped in the review gate — only
 * approved cards render for non-owner users (owners always see everything).
 */

import { useState } from "react";
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { useApiClient } from "../lib/api-context.js";
import { useSetupGate, SetupLocked, PullingData, SyncError } from "../lib/setup-gate.js";
import { Hideable } from "../lib/hide.js";
import { SortableArea } from "../lib/sortable-cards.js";
import { Card, CardHead } from "../components/cards.js";
import { Segmented } from "../components/Segmented.js";
import {
  useSubsQuery, useSubRetentionQuery,
  SubMoneyCard, RecurringSalesCard, UpcomingChargesCard, SubscriptionProjectionCard,
  NetAddsTile, MonthlyChurnTile, ActiveSubsTile,
  SubscriptionLeaksCard, SubsNewVsCanceledCard, ChurnRateByMonthCard, CancelCurveCard, BaseLifetimeCard, SubsByTypeCard, SubCohortMatrixCard,
  subscriptionPlanSourceLabel,
  type SubTypeFilter, type SubResponse, type SubRetention,
} from "../cards/subscriptions.js";
import type { Capabilities } from "../lib/card-requirements.js";
import { useDemoMode } from "../lib/demo-mode.js";
import { PageLoadError } from "../components/PageLoadError.js";
import { pageLoadState } from "../lib/page-load-state.js";

export function BuyGoodsSubscriptionsUnavailable({ detailed = true }: { detailed?: boolean }): React.ReactElement {
  return (
    <div>
      <div style={kicker}>Subscriptions</div>
      <h1 style={{ margin: "7px 0 16px", fontSize: 27 }}>Subscription results are unavailable</h1>
      <Card>
        <CardHead title="The source history does not include complete plan events" />
        <div role="status" style={{ marginTop: 12, color: "var(--body)", fontSize: 13.5, lineHeight: 1.55 }}>
          {detailed
            ? "Some BuyGoods records include recurring charges and their parent links. The source history does not prove plan starts, changes, cancels, churn, or future shipments. The charges stay in sales and order history, but subscription results stay hidden."
            : "We could not confirm the plan events needed for subscription results. Sales and order history stay available, but subscription results stay hidden."}
        </div>
      </Card>
    </div>
  );
}

export function subscriptionPageAvailability(caps: Capabilities | undefined): {
  planState: boolean;
  recurringSales: boolean;
  legacyOnly: boolean;
  any: boolean;
} {
  const planState = caps?.subscriptionPlanState === true;
  const recurringSales = caps?.subscriptionRecurringSales === true;
  const modern = planState || recurringSales;
  const legacyOnly = !modern && caps?.subscriptionLifecycle === true;
  return { planState, recurringSales, legacyOnly, any: modern || legacyOnly };
}

export default function SubscriptionsPage(): React.ReactElement {
  const gate = useSetupGate();
  const api = useApiClient();
  const demo = useDemoMode();
  const ready = gate.data?.previewAllowed === true && gate.data.syncStatus === "ready";
  const capsQ = useQuery({
    queryKey: ["capabilities"],
    queryFn: () => api.get<Capabilities>("/dashboard/capabilities"),
    enabled: ready,
    staleTime: 0,
    refetchOnWindowFocus: true,
  });
  const availability = subscriptionPageAvailability(capsQ.data);
  const subs = useSubsQuery(availability.any);
  const [subType, setSubType] = useState<SubTypeFilter>("all");
  const orderLinkageReady = capsQ.data?.subscriptionOrderLinkage === true;
  const effectiveSubType = orderLinkageReady ? subType : "all";
  const retention = useSubRetentionQuery(effectiveSubType, availability.planState || availability.legacyOnly);
  const gateLoad = pageLoadState(gate);
  const capsLoad = pageLoadState(capsQ);
  const subsLoad = pageLoadState(subs);

  if (gateLoad.failed) return <PageLoadError title="We couldn't check your dashboard status" onRetry={gateLoad.retry} retrying={gateLoad.retrying} />;
  if (gate.isLoading) return <p style={{ color: "var(--body)" }}>Loading…</p>;
  if (gate.data?.locked) return <SetupLocked />;
  if (gate.data?.syncStatus === "error") return <SyncError message={gate.data.errorMessage} />;
  if (gate.data?.syncStatus === "pending") return <PullingData />;
  if (capsLoad.failed) return <PageLoadError title="We couldn't check your subscription sources" onRetry={capsLoad.retry} retrying={capsLoad.retrying} />;
  if (capsQ.isLoading) return <p style={{ color: "var(--body)" }}>Checking subscription sources…</p>;
  if (!availability.any) {
    return <BuyGoodsSubscriptionsUnavailable detailed={capsQ.data?.buyGoodsDetailedHistoryGap === true} />;
  }
  if (subsLoad.failed) return <PageLoadError title="We couldn't load your subscription results" onRetry={subsLoad.retry} retrying={subsLoad.retrying} />;
  if (subs.isLoading || !subs.data) return <p style={{ color: "var(--body)" }}>Loading subscription results…</p>;

  const cmAvailable = (demo || capsQ.data?.financialBasis === "profit") && subs.data.hasCm !== false;
  if (availability.legacyOnly) {
    return (
      <LegacySubscriptionsContent
        data={subs.data}
        cmAvailable={cmAvailable}
        subType={subType}
        setSubType={setSubType}
        retention={retention}
      />
    );
  }
  if (!subs.data.sourceCoverage) {
    return (
      <PageLoadError
        title="We couldn't verify the subscription sources"
        body="The totals stay hidden until their plan and order sources are confirmed. Try again."
        onRetry={() => { void subs.refetch(); }}
        retrying={subs.isFetching}
      />
    );
  }
  const verifiedPlanState = availability.planState && subs.data.sourceCoverage.planState.available === true;
  const verifiedRecurringSales = availability.recurringSales && subs.data.sourceCoverage.recurringSales.available === true;
  if (!verifiedPlanState && !verifiedRecurringSales) {
    return (
      <PageLoadError
        title="We couldn't verify the subscription sources"
        body="The sources changed while this page was loading. Try again."
        onRetry={() => { void subs.refetch(); }}
        retrying={subs.isFetching}
      />
    );
  }

  return (
    <SourceSeparatedSubscriptionsContent
      data={subs.data}
      planStateReady={verifiedPlanState}
      recurringSalesReady={verifiedRecurringSales}
      orderLinkageReady={orderLinkageReady}
      cmAvailable={cmAvailable}
      subType={subType}
      setSubType={setSubType}
      retention={retention}
    />
  );
}

function SourceSeparatedSubscriptionsContent({
  data,
  planStateReady,
  recurringSalesReady,
  orderLinkageReady,
  cmAvailable,
  subType,
  setSubType,
  retention,
}: {
  data: SubResponse;
  planStateReady: boolean;
  recurringSalesReady: boolean;
  orderLinkageReady: boolean;
  cmAvailable: boolean;
  subType: SubTypeFilter;
  setSubType: React.Dispatch<React.SetStateAction<SubTypeFilter>>;
  retention: UseQueryResult<SubRetention, Error>;
}): React.ReactElement {
  const source = data.sourceCoverage!;
  const retentionLoad = pageLoadState(retention);
  const s = data.summary;
  const planLabel = subscriptionPlanSourceLabel(source);
  const planProviders = source.planState.providers.map((provider) => provider.label).join(" and ");
  const orderProviders = source.recurringSales.providers.map((provider) => provider.label).join(" and ");
  const orderLinked = planStateReady && recurringSalesReady && orderLinkageReady && source.orderLinkage.reliable;
  const linkagePct = source.orderLinkage.coverage === null ? "not applicable" : `${(source.orderLinkage.coverage * 100).toFixed(1)}%`;
  const planDataExists = source.planState.totalPlans > 0;

  return (
    <div>
      <div style={kicker}>Subscriptions</div>
      <h1 style={{ margin: "7px 0 8px", fontSize: 27 }}>Subscriptions</h1>
      <p style={{ color: "var(--body)", fontSize: 14, maxWidth: 820, lineHeight: 1.55, margin: "0 0 18px" }}>
        Plan health and recurring-order sales come from different records. This page keeps them separate.
      </p>

      <Card style={{ marginBottom: 24 }}>
        <CardHead title="What these numbers cover" />
        <div role="note" style={{ display: "grid", gap: 8, marginTop: 12, color: "var(--body)", fontSize: 13, lineHeight: 1.55 }}>
          {planStateReady && (
            <div>
              <b style={{ color: "var(--text)" }}>{planLabel} plan health:</b> {source.planState.totalPlans.toLocaleString()} plans from {planProviders || planLabel}. {source.planState.scope === "provider_only"
                ? "This does not include plans managed in other systems."
                : "This is the plan source used for this account."}
            </div>
          )}
          {recurringSalesReady && (
            <div><b style={{ color: "var(--text)" }}>Recurring sales from orders:</b> orders marked recurring by {orderProviders || "connected order sources"}. These order records do not prove plan status.</div>
          )}
          {planStateReady && recurringSalesReady && !orderLinked && (
            <div style={{ color: "var(--text)" }}>
              <b>{source.orderLinkage.linkedPlans.toLocaleString()} of {source.orderLinkage.eligiblePlans.toLocaleString()} {planLabel} plans match imported orders ({linkagePct}).</b>{" "}
              Forecasts, product and SKU breakdowns, subscriber rate, subscription value, collection timing, and contribution margin stay hidden.
            </div>
          )}
        </div>
      </Card>

      {planStateReady && (
        <section aria-labelledby="plan-health-title" style={{ marginBottom: 30 }}>
          <h2 id="plan-health-title" style={sectionTitle}>{planLabel} plan health</h2>
          <p style={sectionBody}>Plan status, starts, endings, and future charge dates reported by {planProviders || planLabel} only.</p>
          {!planDataExists ? (
            <Card>
              <CardHead title={`No ${planLabel} plans found`} />
              <p style={{ color: "var(--body)", fontSize: 13.5, lineHeight: 1.55, margin: "12px 0 0" }}>The connection is ready, but it has not returned any plans yet.</p>
            </Card>
          ) : (
            <SortableArea pageKey="subscriptions">
              <Hideable id="subscription-upcoming-charges" label={`${planLabel} upcoming charges`} span={12}>
                <UpcomingChargesCard s={s} coverage={source.scheduledDates} sourceLabel={planLabel} />
              </Hideable>

              {orderLinked && source.forecastCompatible && data.rebills.projected30 !== null && (
                <Hideable id="subscription-projection" label={`${planLabel} recurring-sales forecast`} span={12}>
                  <SubscriptionProjectionCard rb={data.rebills} sourceLabel={planLabel} />
                </Hideable>
              )}

              <Hideable id="net-adds" label={`${planLabel} plan net adds`} span={4}><NetAddsTile s={s} sourceLabel={planLabel} /></Hideable>
              <Hideable id="monthly-churn" label={`${planLabel} plan churn`} span={4}><MonthlyChurnTile s={s} sourceLabel={planLabel} /></Hideable>
              <Hideable id="active-subs" label={`Active ${planLabel} plans`} span={4}><ActiveSubsTile s={s} sourceLabel={planLabel} /></Hideable>

              <Hideable id="subscription-leaks" label={`${planLabel} plan losses`} span={12}>
                <SubscriptionLeaksCard leaks={data.leaks} cmAvailable={orderLinked && cmAvailable} sourceLabel={planLabel} />
              </Hideable>

              {orderLinked && (
                <Hideable id="subs-by-type" label={`${planLabel} subscriptions by type`} span={12}>
                  <SubsByTypeCard rows={data.byType} cmAvailable={cmAvailable} sourceLabel={planLabel} style={{ marginBottom: 14 }} />
                </Hideable>
              )}

              <Hideable id="subs-new-vs-canceled" label={`${planLabel} starts vs ends`} span={6}><SubsNewVsCanceledCard trend={data.trend} sourceLabel={planLabel} /></Hideable>
              <Hideable id="churn-rate-by-month" label={`${planLabel} churn by month`} span={6}><ChurnRateByMonthCard churnTrend={data.churnTrend} sourceLabel={planLabel} /></Hideable>

              <div data-card-id="retention-header" data-span={12} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap", marginTop: 6 }}>
                <h3 style={{ fontSize: 16, margin: 0 }}>{planLabel} retention detail</h3>
                {orderLinked && <Segmented<SubTypeFilter> value={subType} onChange={setSubType}
                  options={[{ value: "all", label: "All plans" }, { value: "physical", label: "Physical" }, { value: "app", label: "App" }]} />}
              </div>

              {retentionLoad.failed && (
                <div data-card-id="retention-error" data-span={12}>
                  <PageLoadError title={`We couldn't load ${planLabel} retention details`} onRetry={retentionLoad.retry} retrying={retentionLoad.retrying} />
                </div>
              )}
              {!retentionLoad.failed && <Hideable id="cancel-curve" label={`${planLabel} cancel timing`} span={6}><CancelCurveCard curve={retention.data?.curve} sourceLabel={planLabel} /></Hideable>}
              <Hideable id={orderLinked ? "base-lifetime" : "subscription-plan-duration"} label={`${planLabel} plan duration`} span={retentionLoad.failed ? 12 : 6}><BaseLifetimeCard s={s} value={data.subscriptionValue} cmAvailable={orderLinked && cmAvailable} orderLinked={orderLinked} sourceLabel={planLabel} /></Hideable>
              {!retentionLoad.failed && <Hideable id="sub-cohort-matrix" label={`${planLabel} plan retention by cohort`} span={12}><SubCohortMatrixCard cohorts={retention.data?.cohorts} sourceLabel={planLabel} /></Hideable>}
            </SortableArea>
          )}
        </section>
      )}

      {recurringSalesReady && (
        <section aria-labelledby="recurring-sales-title">
          <h2 id="recurring-sales-title" style={sectionTitle}>Recurring sales from orders</h2>
          <p style={sectionBody}>
            Actual recurring-order sales from {orderProviders || "connected order sources"}. {planStateReady
              ? `These are not presented as ${planLabel} plan revenue.`
              : "These order records do not prove plan status or future charges."}
          </p>
          {/* This has its own saved sort order, but approval still belongs to the
              Subscriptions page and deliberately reuses the approved sub-money id. */}
          <SortableArea pageKey="subscriptions-recurring-sales">
            <Hideable id="sub-money-30d" label="Recurring sales from orders" span={12}>
              <RecurringSalesCard rb={data.rebills} coverage={source.recurringSales} />
            </Hideable>
          </SortableArea>
        </section>
      )}
    </div>
  );
}

function LegacySubscriptionsContent({
  data,
  cmAvailable,
  subType,
  setSubType,
  retention,
}: {
  data: SubResponse;
  cmAvailable: boolean;
  subType: SubTypeFilter;
  setSubType: React.Dispatch<React.SetStateAction<SubTypeFilter>>;
  retention: UseQueryResult<SubRetention, Error>;
}): React.ReactElement {
  const s = data.summary;
  const retentionLoad = pageLoadState(retention);
  return (
    <div>
      <div style={kicker}>Subscriptions</div>
      <h1 style={{ margin: "7px 0 8px", fontSize: 27 }}>How are your subscriptions doing?</h1>
      {s.totalSubs === 0 ? (
        <Card style={{ marginTop: 20 }}><CardHead title="No subscriptions yet" /></Card>
      ) : (
        <SortableArea pageKey="subscriptions">
          <Hideable id="sub-money-30d" label="Subscription summary" span={12}><SubMoneyCard s={s} rb={data.rebills} cmAvailable={cmAvailable} style={{ marginTop: 10 }} /></Hideable>
          <Hideable id="net-adds" label="Net adds" span={4}><NetAddsTile s={s} /></Hideable>
          <Hideable id="monthly-churn" label="Churn rate" span={4}><MonthlyChurnTile s={s} /></Hideable>
          <Hideable id="active-subs" label="Active subscriptions" span={4}><ActiveSubsTile s={s} /></Hideable>
          <Hideable id="subscription-leaks" label="Subscription leaks" span={12}><SubscriptionLeaksCard leaks={data.leaks} cmAvailable={cmAvailable} /></Hideable>
          <Hideable id="subs-by-type" label="Subscriptions by type" span={12}><SubsByTypeCard rows={data.byType} cmAvailable={cmAvailable} style={{ marginBottom: 14 }} /></Hideable>
          <Hideable id="subs-new-vs-canceled" label="New vs ended" span={6}><SubsNewVsCanceledCard trend={data.trend} /></Hideable>
          <Hideable id="churn-rate-by-month" label="Churn rate by month" span={6}><ChurnRateByMonthCard churnTrend={data.churnTrend} /></Hideable>
          <div data-card-id="retention-header" data-span={12} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap", marginTop: 6 }}>
            <h2 style={{ fontSize: 16, margin: 0 }}>Retention deep-dive</h2>
            <Segmented<SubTypeFilter> value={subType} onChange={setSubType} options={[{ value: "all", label: "All plans" }, { value: "physical", label: "Physical" }, { value: "app", label: "App" }]} />
          </div>
          {retentionLoad.failed && (
            <div data-card-id="retention-error" data-span={12}><PageLoadError title="We couldn't load subscription retention details" onRetry={retentionLoad.retry} retrying={retentionLoad.retrying} /></div>
          )}
          {!retentionLoad.failed && <Hideable id="cancel-curve" label="Cancel curve" span={6}><CancelCurveCard curve={retention.data?.curve} /></Hideable>}
          <Hideable id="base-lifetime" label="Subscription summary" span={retentionLoad.failed ? 12 : 6}><BaseLifetimeCard s={s} value={data.subscriptionValue} cmAvailable={cmAvailable} /></Hideable>
          {!retentionLoad.failed && <Hideable id="sub-cohort-matrix" label="Cohort retention" span={12}><SubCohortMatrixCard cohorts={retention.data?.cohorts} /></Hideable>}
        </SortableArea>
      )}
    </div>
  );
}

const kicker: React.CSSProperties = { color: "var(--accent)", fontSize: 12, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase" };
const sectionTitle: React.CSSProperties = { margin: "0 0 5px", fontSize: 20 };
const sectionBody: React.CSSProperties = { margin: "0 0 14px", color: "var(--body)", fontSize: 13.5, lineHeight: 1.5 };
